import { alpha } from '@mui/material/styles';
import chroma from 'chroma-js';

import { Palette } from '@sumologic/yama-ds-components/styles';

import { ThemeConfiguration } from 'shared/components/utils/Theme';

export const getLegacyCybertronTheme = (
  palette: Palette
): ThemeConfiguration => {
  const {
    background,
    common,
    error,
    info,
    primary,
    success,
    text,
    tokens,
    warning,
  } = palette;

  return {
    name: 'cybertron',
    variables: {
      '--color-common-white': common.white,
      '--color-grey-500': tokens.YamaPaletteGrey500Color,
      '--color-grey-700': tokens.YamaPaletteGrey700Color,
      '--color-primary-main': primary.main,
      '--color-primary-light': primary.light,
      '--color-text-primary': text.primary,
      '--color-text-secondary': text.secondary,
      '--color-text-error': tokens.YamaTypographyColorError,
      '--color-text-contrast': tokens.YamaPaletteTextContrastColor,
      '--color-error-light': error.light,
      '--color-error-main': error.main,
      '--color-error-dark': error.dark,
      '--color-error-contrast-text': error.contrastText,
      '--color-warning-light': warning.light,
      '--color-warning-main': warning.main,
      '--color-icon': tokens.YamaTypographyColorIcon,
      '--color-info-contrast-text': info.contrastText,
      '--color-info-light': info.light,
      '--color-info-main': info.main,
      '--color-info-dark': info.dark,
      '--color-success-main': success.main,
      '--color-background-paper': background.paper,
      '--color-background-default': background.default,
      '--color-background-chip':
        tokens.YamaChipVariantFilledStateDefaultBackgroundColor,
      '--color-background-contrast':
        tokens.YamaTooltipVariantDefaultBackgroundColor,
      '--color-link-hover': tokens.YamaLinkPrimaryHoverColor,
      '--color-border': tokens.YamaBorderVariantLowcontrastColor,

      /**
       * Yama tiles (used for CSIEM cards)
       */
      '--yama-tile-state-default-border-color':
        tokens.YamaTileStateDefaultBorderColor,
      '--yama-tile-state-default-background-color':
        tokens.YamaTileStateDefaultBackgroundColor,
      '--yama-tile-state-hover-background-color':
        tokens.YamaTileStateHoverBackgroundColor,
      '--yama-tile-state-hover-border-color':
        tokens.YamaTileStateHoverBorderColor,
      '--yama-tile-state-hover-box-shadow': tokens.YamaTileStateHoverBoxShadow,

      /**
       * Yama dropzone (used for CSIEM dropzone)
       * TODO: Replace this token with dropzone tokens when available
       */
      '--yama-dropzone-background-color':
        tokens.YamaApplicationTertiaryBackgroundColor,
      '--yama-dropzone-border-color': tokens.YamaBorderVariantLowcontrastColor,

      /**
       * Yama code editor (used for code editor backgrounds and borders)
       */
      '--yama-code-editor-background-main-color':
        tokens.YamaCodeEditorBackgroundMainColor,
      '--yama-code-editor-border-editor-color':
        tokens.YamaCodeEditorBorderEditorColor,

      /**
       * Yama code editor (used for code editor backgrounds and borders)
       */
      '--yama-menu-item-state-default-background-color':
        tokens.YamaMenuItemStateDefaultBackgroundColor,
      '--yama-menu-item-state-hover-background-color':
        tokens.YamaMenuItemStateHoverBackgroundColor,

      /**
       * HUD
       */
      '--color-hud-gradient': alpha(primary.main, 0.3),
      '--color-hud-insight': tokens.YamaChartColorsDanger,
      '--color-hud-signal': tokens.YamaColorAccentOcean40,
      '--color-hud-record': tokens.YamaPalettePrimaryMainColor,

      /**
       * HUD lower bar on Created Insights and unfilled label meter bar
       */
      '--color-error-dark-half-opacity': alpha(error.dark, 0.5),

      /**
       * Signal Chart colors
       */
      '--color-signal-chart-anomaly': tokens.YamaColorAccentEvergreen30,
      '--color-signal-chart-rule': tokens.YamaColorAccentOcean70,
      '--color-signal-chart-file-analysis': tokens.YamaColorLiteralWarning30,
      '--color-signal-chart-threat-intelligence':
        tokens.YamaColorAccentMagenta70,

      /**
       * Layered elements that lay on top of background and
       * surface such as tooltips and notifications.
       * Used by Bridge
       */
      '--color-layer-transparent': chroma(background.default)
        .darken(0.8)
        .alpha(0.9)
        .css(),

      /**
       * Content Drawer
       */
      '--color-background-default-half-alpha': chroma(background.default)
        .alpha(0.5)
        .css(),

      /**
       * Used by reaviz
       */
      '--color-background': background.default,
      '--color-primary': primary.main,
      '--color-handle-fill': background.default,
      '--color-handle-stroke': primary.main,
      '--color-handle-drag-fill': background.paper,
      '--color-handle-dots': tokens.YamaTypographyColorIcon,
      '--color-handle-line': primary.main,
    },
    palettes: {
      /**
       * Identicon base colors
       */
      identicon: [
        tokens.YamaColorAccentMagenta90,
        tokens.YamaColorAccentEvergreen30,
        tokens.YamaColorLiteralWarning30,
        tokens.YamaColorAccentOcean70,
        tokens.YamaColorAccentEvergreen30,
      ],
    },
    gradients: {
      insightScatterPlotAxis: [
        {
          offset: '1%',
          stopOpacity: 0.9,
          color: tokens.YamaColorBaseBlack90,
        },
        {
          offset: '30%',
          stopOpacity: 1,
          color: tokens.YamaColorBaseBlack20,
        },
        {
          offset: '50%',
          stopOpacity: 1,
          color: tokens.YamaColorLiteralWarning30,
        },
        {
          offset: '60%',
          stopOpacity: 1,
          color: tokens.YamaColorLiteralWarning50,
        },
        {
          offset: '80%',
          stopOpacity: 1,
          color: tokens.YamaColorLiteralCritical70,
        },
      ],
    },
  };
};
